import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { FiArrowRight, FiArrowUpRight, FiMapPin, FiBriefcase, FiMail } from "react-icons/fi";
import Seo from "../../components/Seo/Seo";
import Button from "../../components/Button/Button";
import Reveal from "../../components/Reveal/Reveal";
import SectionHeader from "../../components/SectionHeader/SectionHeader";
import ProjectCard from "../../components/ProjectCard/ProjectCard";
import SkillMeter from "../../components/SkillMeter/SkillMeter";
import Timeline from "../../components/Timeline/Timeline";
import CtaBanner from "../../components/CtaBanner/CtaBanner";
import { profile } from "../../data/profile";
import { skills, toolGroups } from "../../data/skills";
import { experience } from "../../data/career";
import { featuredProjects, projects } from "../../data/projects";
import { services } from "../../data/services";
import { stats } from "../../data/stats";
import "./Home.scss";

const coreStack = ["React Native", "Node.js", "Express", "MongoDB", "MySQL", "AWS", "Figma"];

const pillars = [
  { title: "Mobile", body: "React Native and JavaScript for Android and iOS." },
  { title: "Backend", body: "Node.js and Express APIs behind the apps." },
  { title: "Data", body: "MongoDB and MySQL modelling and management." },
  { title: "Cloud & Design", body: "AWS deployments and Figma UI design." },
];

const Hero = () => {
  const reduce = useReducedMotion();
  const fade = (delay) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay },
        };
  const current = experience.find((e) => e.current);

  return (
    <section className="hero" aria-labelledby="hero-title">
      <div className="hero__bg" aria-hidden="true" />
      <div className="container hero__grid">
        <div className="hero__text">
          <motion.p className="eyebrow hero__eyebrow" {...fade(0)}>
            <span className="hero__pulse" aria-hidden="true" />
            {profile.role}
          </motion.p>
          <motion.h1 id="hero-title" className="hero__title" {...fade(0.08)}>
            I build mobile apps{" "}
            <span className="hero__title-muted">and the backends that run them.</span>
          </motion.h1>
          <motion.p className="hero__lead" {...fade(0.16)}>
            I'm {profile.name}. {profile.summary}
          </motion.p>
          <motion.div className="hero__actions" {...fade(0.24)}>
            <Button to="/projects" size="lg" icon={FiArrowRight}>
              View My Work
            </Button>
            <Button to="/contact" size="lg" variant="secondary">
              Let's Work Together
            </Button>
          </motion.div>
          <motion.dl className="hero__stats" {...fade(0.32)}>
            {stats().map((s) => (
              <div key={s.label} className="hero__stat">
                <dt>{s.label}</dt>
                <dd>{s.value}</dd>
              </div>
            ))}
          </motion.dl>
        </div>

        <motion.aside
          className="hero-panel"
          aria-label="Profile summary"
          {...(reduce
            ? {}
            : {
                initial: { opacity: 0, y: 24, scale: 0.98 },
                animate: { opacity: 1, y: 0, scale: 1 },
                transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 },
              })}
        >
          <div className="hero-panel__head">
            <img
              src={profile.photo}
              alt={`Portrait of ${profile.name}`}
              width="56"
              height="56"
              className="hero-panel__avatar"
            />
            <div>
              <p className="hero-panel__name">{profile.name}</p>
              <p className="hero-panel__role">{profile.role}</p>
            </div>
          </div>
          <dl className="hero-panel__rows">
            {current && (
              <div className="hero-panel__row">
                <dt>
                  <FiBriefcase aria-hidden="true" /> Currently
                </dt>
                <dd>
                  {current.role}, {current.company}
                </dd>
              </div>
            )}
            <div className="hero-panel__row">
              <dt>
                <FiMapPin aria-hidden="true" /> Based in
              </dt>
              <dd>{profile.location}</dd>
            </div>
            <div className="hero-panel__row">
              <dt>
                <FiMail aria-hidden="true" /> Email
              </dt>
              <dd>
                <a href={`mailto:${profile.email}`} className="text-link">
                  {profile.email}
                </a>
              </dd>
            </div>
          </dl>
          <div className="hero-panel__stack">
            <p className="hero-panel__label">Core stack</p>
            <ul className="tag-list">
              {coreStack.map((t) => (
                <li key={t} className="tag">
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </motion.aside>
      </div>
    </section>
  );
};

const Home = () => {
  const tools = toolGroups.flatMap((g) => g.items);
  const timelineItems = experience.map((e) => ({
    key: e.company,
    period: `${e.start} – ${e.end}`,
    current: e.current,
    title: e.role,
    subtitle: e.company,
    meta: e.location,
  }));

  return (
    <>
      <Seo />
      <Hero />

      {/* About teaser */}
      <section className="section section--divided" aria-labelledby="about-title">
        <div className="container home-about">
          <div className="home-about__text">
            <SectionHeader
              id="about-title"
              eyebrow="About"
              title="One engineer across the whole mobile stack"
            />
            <Reveal className="home-about__body">
              <p>{profile.bio[0]}</p>
              <Link to="/about" className="text-link home-about__link">
                More about me <FiArrowRight aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
          <ul className="home-about__pillars">
            {pillars.map((p, i) => (
              <Reveal as="li" key={p.title} delay={i * 0.06} className="pillar">
                <h3 className="pillar__title">{p.title}</h3>
                <p className="pillar__body">{p.body}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Skills teaser */}
      <section className="section section--divided" aria-labelledby="skills-title">
        <div className="container">
          <SectionHeader
            id="skills-title"
            eyebrow="Skills"
            title="Core stack"
            description="Self-assessed proficiency across the tools I use most."
            action={
              <Button to="/about" variant="secondary" icon={FiArrowRight}>
                Skills & tools
              </Button>
            }
          />
          <Reveal className="home-skills">
            {skills.map((s) => (
              <SkillMeter key={s.name} name={s.name} level={s.level} icon={s.icon} />
            ))}
          </Reveal>
          <Reveal className="home-tools">
            <p className="home-tools__label">Also worked with</p>
            <ul className="tag-list">
              {tools.map(({ name, icon: Icon }) => (
                <li key={name} className="tag">
                  <Icon aria-hidden="true" />
                  {name}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Featured projects */}
      <section className="section section--divided" aria-labelledby="work-title">
        <div className="container">
          <SectionHeader
            id="work-title"
            eyebrow="Selected work"
            title="Featured projects"
            description="A few of the production apps I've built. Each one has a short case study."
            action={
              <Button to="/projects" variant="secondary" icon={FiArrowRight}>
                All {projects.length} projects
              </Button>
            }
          />
          <div className="home-work">
            {featuredProjects.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 2) * 0.08}>
                <ProjectCard project={p} size="large" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Experience teaser */}
      <section className="section section--divided" aria-labelledby="exp-title">
        <div className="container home-exp">
          <SectionHeader
            id="exp-title"
            eyebrow="Experience"
            title="Where I've worked"
            description="Mobile application development roles in Tamil Nadu since 2022."
            action={
              <Button to="/experience" variant="secondary" icon={FiArrowRight}>
                Full timeline
              </Button>
            }
          />
          <Timeline items={timelineItems} compact />
        </div>
      </section>

      <CtaBanner
        eyebrow="Services"
        title="From Figma screens to the Play Store"
        description={`Mobile, backend, database, design and deployment work: ${services.length} services, delivered by one engineer.`}
      >
        <Button to="/services" icon={FiArrowRight}>
          Explore services
        </Button>
        <Button href={profile.resumeUrl} variant="secondary" icon={FiArrowUpRight} aria-label="Resume (PDF, opens in a new tab)">
          Resume
        </Button>
      </CtaBanner>
    </>
  );
};

export default Home;
